import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useBranches } from "../hooks/use-branches";
import { useCiRunIds } from "../hooks/use-ci-run-ids";
import { useShardTotals } from "../hooks/use-shard-totals";
import { TestRunStatusList } from "../types";
import { SearchBar } from "./search-bar";

// Helper to get CI provider display name from ci_run_id
function getCiProviderLabel(ciRunId: string): string {
  if (ciRunId.startsWith("gh-")) return "GitHub";
  if (ciRunId.startsWith("gl-")) return "GitLab";
  if (ciRunId.startsWith("cci-")) return "CircleCI";
  if (ciRunId.startsWith("jenkins-")) return "Jenkins";
  if (ciRunId.startsWith("azdo-")) return "Azure";
  if (ciRunId.startsWith("travis-")) return "Travis";
  if (ciRunId.startsWith("bk-")) return "Buildkite";
  return "CI";
}

interface SuiteRunsFiltersProps {
  searchQuery: string;
  statusFilter: string;
  branchFilter?: string;
  ciRunIdFilter?: string;
  shardFilter?: string;
  onSearchChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onBranchChange?: (value: string) => void;
  onCiRunIdChange?: (value: string) => void;
  onShardChange?: (value: string) => void;
}

export const SuiteRunsFilters = ({
  searchQuery,
  statusFilter,
  branchFilter = "",
  ciRunIdFilter = "",
  shardFilter = "",
  onSearchChange,
  onStatusChange,
  onBranchChange,
  onCiRunIdChange,
  onShardChange,
}: SuiteRunsFiltersProps) => {
  const { data: branches = [] } = useBranches();
  const { data: ciRunIds = [], isLoading: isLoadingCiRunIds } = useCiRunIds();
  const { data: shardTotals = [], isLoading: isLoadingShards } = useShardTotals();

  return (
    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
      {/* Search - existing */}
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <SearchBar
          size="small"
          value={searchQuery}
          onChange={onSearchChange}
          label="Search by suite name, status, or ID..."
          sx={{
            width: "100%",
            "& .MuiInputLabel-root": {
              fontSize: "0.8rem",
              color: "text.secondary",
            },
            "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "divider",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "info.dark",
            },
          }}
        />
      </Box>

      {/* Status - existing */}
      <Box>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel
            sx={{
              fontSize: "0.8rem",
              color: "text.secondary",
            }}
          >
            Status
          </InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => onStatusChange(e.target.value)}
            sx={{
              "& .MuiSelect-select": {
                fontSize: "0.8rem",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "divider",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "info.dark",
              },
            }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            {TestRunStatusList.map((status) => (
              <MenuItem key={status} value={status}>
                {status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Branch - existing */}
      {branches.length > 0 && onBranchChange && (
        <Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              Branch
            </InputLabel>
            <Select
              value={branchFilter}
              label="Branch"
              onChange={(e) => onBranchChange(e.target.value)}
              sx={{
                "& .MuiSelect-select": {
                  fontSize: "0.8rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "info.dark",
                },
              }}
            >
              <MenuItem value="">All Branches</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch} value={branch}>
                  {branch}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* CI Run ID - NEW */}
      {onCiRunIdChange && (
        <Box sx={{ minWidth: 200 }}>
          <Autocomplete
            size="small"
            freeSolo
            options={ciRunIds}
            value={ciRunIdFilter || null}
            loading={isLoadingCiRunIds}
            onChange={(_, newValue) => {
              onCiRunIdChange(newValue || "");
            }}
            onInputChange={(_, newInputValue, reason) => {
              // Only update on user input, not on selection
              if (reason === "input") {
                onCiRunIdChange(newInputValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="CI Run ID"
                placeholder="e.g., gh-12345-1"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingCiRunIds && <CircularProgress color="inherit" size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "text.secondary",
                  },
                  "& .MuiOutlinedInput-input": { fontSize: "0.8rem" },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "divider",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "info.dark",
                  },
                }}
              />
            )}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              return (
                <Box
                  component="li"
                  key={key}
                  {...otherProps}
                  sx={{ display: "flex", gap: 1, alignItems: "center" }}
                >
                  <Chip
                    label={getCiProviderLabel(option)}
                    size="small"
                    sx={{ fontSize: "0.7rem" }}
                  />
                  <span style={{ fontSize: "0.8rem" }}>{option}</span>
                </Box>
              );
            }}
            noOptionsText="No CI runs found"
            loadingText="Loading CI runs..."
          />
        </Box>
      )}

      {/* Shard Filter - NEW */}
      {shardTotals.length > 0 && onShardChange && (
        <Box>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel
              sx={{
                fontSize: "0.8rem",
                color: "text.secondary",
              }}
            >
              Shards
            </InputLabel>
            <Select
              value={shardFilter}
              label="Shards"
              onChange={(e) => onShardChange(e.target.value)}
              disabled={isLoadingShards}
              sx={{
                "& .MuiSelect-select": {
                  fontSize: "0.8rem",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "info.dark",
                },
              }}
            >
              <MenuItem value="">All Shards</MenuItem>
              {shardTotals.map((total) => (
                <MenuItem key={total} value={String(total)}>
                  {total} shards
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
    </Stack>
  );
};
